import { useMemo } from 'react';
import { Box, Chip, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

const PP = "'Poppins', sans-serif";
const DAY = 24 * 60 * 60 * 1000;

/* ============================================================================
   SCHEDULING — THE MONTH VIEW

   The table answers "what is the state of this job"; a month grid answers "what
   is happening that week", which is the question somebody asks before they
   promise a customer a date. (user 2026-10-05: "can we have mode of view one is
   table view, one is calendar view?")

   It draws the three dates that are appointments — site visit, delivery,
   installation — because those are the ones a crew turns up for. TNB approval
   is a letter arriving, so it stays in the table.
   ========================================================================= */

const KINDS = [
    { key: 'siteVisitOn', label: 'Site visit', short: 'Site', bg: '#FEF3C7', fg: '#92400E', bar: '#F59E0B' },
    { key: 'deliveryOn', label: 'Delivery', short: 'Deliv', bg: '#E0F2FE', fg: '#075985', bar: '#0EA5E9' },
    { key: 'installationOn', label: 'Installation', short: 'Install', bg: '#DCFCE7', fg: '#166534', bar: '#16A34A' },
];

const startOfDay = (d) => { const x = new Date(d); x.setHours(0, 0, 0, 0); return x; };
const keyOf = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const asDate = (v) => {
    if (!v) return null;
    const d = new Date(v);
    return isNaN(d.getTime()) ? null : startOfDay(d);
};

/** The Monday on or before the 1st, so every month starts on the same column. */
const gridStart = (monthStart) => {
    const d = new Date(monthStart);
    const back = (d.getDay() + 6) % 7;        // Monday = 0
    d.setDate(d.getDate() - back);
    return startOfDay(d);
};

export default function SchedulingCalendar({ rows, month, onMonth, onOpen, today }) {
    // Every appointment in view, bucketed by the day it falls on. Built once per
    // month change rather than per cell — 600 rows × 42 cells is not a lookup to
    // repeat 42 times.
    const byDay = useMemo(() => {
        const out = {};
        rows.forEach(r => {
            KINDS.forEach(k => {
                const d = asDate(r[k.key]);
                if (!d) return;
                (out[keyOf(d)] ||= []).push({ kind: k, row: r });
            });
        });
        // Installations first inside a day, then delivery, then site visit: the one
        // with a crew attached is the one to read first.
        Object.values(out).forEach(list => list.sort((a, b) =>
            KINDS.indexOf(b.kind) - KINDS.indexOf(a.kind)
            || (a.row.customerName || '').localeCompare(b.row.customerName || '')));
        return out;
    }, [rows]);

    const monthStart = startOfDay(new Date(month.getFullYear(), month.getMonth(), 1));
    const first = gridStart(monthStart);
    const cells = Array.from({ length: 42 }, (_, i) => new Date(first.getTime() + i * DAY));
    // A sixth row that is entirely next month is wasted space.
    const weeks = cells[35].getMonth() === monthStart.getMonth() ? 6 : 5;

    const monthCount = cells.slice(0, weeks * 7)
        .reduce((n, d) => n + (byDay[keyOf(d)]?.length || 0), 0);

    const step = (delta) => onMonth(new Date(month.getFullYear(), month.getMonth() + delta, 1));

    return (
        <Box>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
                <IconButton size="small" onClick={() => step(-1)}
                    sx={{ border: '1px solid #E2E8F0', borderRadius: '8px' }}>
                    <ChevronLeftIcon sx={{ fontSize: 18, color: '#64748B' }} />
                </IconButton>
                <Typography sx={{ fontFamily: PP, fontWeight: 700, fontSize: '0.95rem', color: '#0F172A', minWidth: 170 }}>
                    {month.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' })}
                </Typography>
                <IconButton size="small" onClick={() => step(1)}
                    sx={{ border: '1px solid #E2E8F0', borderRadius: '8px' }}>
                    <ChevronRightIcon sx={{ fontSize: 18, color: '#64748B' }} />
                </IconButton>
                <Chip label="Today" size="small" onClick={() => onMonth(new Date(today.getFullYear(), today.getMonth(), 1))}
                    sx={{ fontFamily: PP, fontSize: '0.72rem', fontWeight: 600, borderRadius: '8px', bgcolor: '#F1F5F9', color: '#475569' }} />
                <Box sx={{ flex: 1 }} />
                <Typography sx={{ fontFamily: PP, fontSize: '0.75rem', color: '#64748B' }}>
                    {monthCount} appointment{monthCount === 1 ? '' : 's'} this month
                </Typography>
                {KINDS.map(k => (
                    <Stack key={k.key} direction="row" alignItems="center" spacing={0.5}>
                        <Box sx={{ width: 9, height: 9, borderRadius: '2px', bgcolor: k.bar }} />
                        <Typography sx={{ fontFamily: PP, fontSize: '0.7rem', color: '#64748B' }}>{k.label}</Typography>
                    </Stack>
                ))}
            </Stack>

            <Box sx={{
                display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)',
                border: '1px solid #E2E8F0', borderRadius: '12px', overflow: 'hidden', bgcolor: '#E2E8F0', gap: '1px',
            }}>
                {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map(d => (
                    <Box key={d} sx={{ bgcolor: '#F8FAFC', py: 0.75, textAlign: 'center' }}>
                        <Typography sx={{ fontFamily: PP, fontSize: '0.7rem', fontWeight: 700, color: '#475569' }}>{d}</Typography>
                    </Box>
                ))}

                {cells.slice(0, weeks * 7).map((d) => {
                    const items = byDay[keyOf(d)] || [];
                    const outside = d.getMonth() !== monthStart.getMonth();
                    const isToday = keyOf(d) === keyOf(today);
                    const weekend = d.getDay() === 0 || d.getDay() === 6;
                    return (
                        <Box key={keyOf(d)} sx={{
                            bgcolor: outside ? '#FBFCFD' : weekend ? '#FAFBFC' : '#fff',
                            minHeight: 104, p: 0.75, display: 'flex', flexDirection: 'column', gap: 0.4,
                        }}>
                            <Typography sx={{
                                fontFamily: PP, fontSize: '0.72rem', fontWeight: isToday ? 800 : 600,
                                color: outside ? '#CBD5E1' : isToday ? '#1A8CFF' : '#475569',
                                display: 'flex', alignItems: 'center', gap: 0.5,
                            }}>
                                {d.getDate()}
                                {isToday && <Box sx={{ width: 5, height: 5, borderRadius: '50%', bgcolor: '#1A8CFF' }} />}
                            </Typography>

                            {items.slice(0, 3).map(({ kind, row }, i) => {
                                const pending = row._remarks?.some(m => m.key === 'pendingPay');
                                // An installation the customer has not confirmed is drawn
                                // hollow, so a tentative date never reads like an agreed one
                                // at a glance. (user 2026-10-05)
                                const tentative = kind.key === 'installationOn' && !row.installationConfirmedOn;
                                return (
                                    <Tooltip key={`${row.salesId}-${kind.key}-${i}`}
                                        title={`${kind.label} · ${row.customerName} · ${row.agentName}`
                                            + (tentative ? ' · TENTATIVE, customer has not confirmed' : '')
                                            + (pending ? ' · pending payment' : '')}>
                                        <Box
                                            onClick={() => onOpen(row)}
                                            sx={{
                                                cursor: 'pointer', borderRadius: '6px', px: 0.6, py: 0.3,
                                                bgcolor: tentative ? 'transparent' : kind.bg,
                                                border: tentative ? `1px dashed ${kind.bar}` : 'none',
                                                borderLeft: `3px solid ${pending ? '#DC2626' : kind.bar}`,
                                                '&:hover': { filter: 'brightness(0.96)' },
                                            }}
                                        >
                                            <Typography sx={{
                                                fontFamily: PP, fontSize: '0.66rem', fontWeight: 700, color: kind.fg,
                                                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                                            }}>
                                                {row.customerName}
                                            </Typography>
                                            <Typography sx={{
                                                fontFamily: PP, fontSize: '0.6rem', color: kind.fg, opacity: 0.8,
                                                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                                            }}>
                                                {kind.short} · {row.agentName}
                                            </Typography>
                                        </Box>
                                    </Tooltip>
                                );
                            })}

                            {items.length > 3 && (
                                <Typography
                                    onClick={() => onOpen(items[3].row)}
                                    sx={{ fontFamily: PP, fontSize: '0.62rem', color: '#64748B', cursor: 'pointer', pl: 0.6 }}
                                >
                                    +{items.length - 3} more
                                </Typography>
                            )}
                        </Box>
                    );
                })}
            </Box>
        </Box>
    );
}
